import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import TestimonialCard from '../../../components/common/TestimonialCard';
import { Images } from '../../../utils/Images';
import { useSelector } from 'react-redux';

const TestimonialSection = () => {
  const theme = useTheme();
  const scrollRef = useRef(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const home = useSelector((state) => state.cms.home);

  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 65);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const testimonials = related
      .filter(r => r && (r.status === 1 || r.status === true))
      .map((r, idx) => ({
        id: r.id ?? idx,
        name: r.title || '',
        description: r.description || '',
        image: r.image || null,
      }));
    return { title: section?.title || 'How Do Donors Describe Their Experience With Our NGO?', testimonials };
  }, [home]);

  const cardWidth = isMobile ? 260 : 300;
  const gap = isMobile ? 16 : 24;

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanGoBack(el.scrollLeft > 2);
    setCanGoNext(el.scrollLeft < maxScroll - 2);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, [updateScrollButtons, cms.testimonials.length]);

  const scrollByCards = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
  };

  const handleNext = () => scrollByCards(1);
  const handlePrevious = () => scrollByCards(-1);

  return (
    <Box
      sx={{
        width: isMobile ? 'calc(100% - 32px)' : '94.44%',
        maxWidth: '1440px',
        borderRadius: isMobile ? '16px' : '36px',
        backgroundColor: 'rgba(232, 232, 232, 0.36)',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: isMobile ? '15px' : '15px',
        mt: isMobile ? '40px' : '40px',
        padding: isMobile ? '30px 16px 24px' : '70px 29px 40px 29px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top-right string hand decoration */}
      {!isMobile && (
        <img
          src={Images.stringHand}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50px',
            right: '0px',
            width: '111px',
            height: '94px',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* Top-left sparkle decoration */}
      {!isMobile && (
        <img
          src={Images.orangeSparks}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '60px',
            left: '110px',
            width: '49.8429px',
            height: '71.2042px',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Red Hat Display',
          fontWeight: 800,
          fontSize: isMobile ? '20px' : isTablet ? '28px' : '36px',
          lineHeight: isMobile ? '28px' : isTablet ? '36px' : '46px',
          textAlign: 'center',
          textTransform: 'capitalize',
          color: '#1D1D1B',
          marginBottom: isMobile ? '20px' : '60px',
          maxWidth: isMobile ? '293px' : isTablet ? '500px' : '738px',
        }}
      >
        {cms.title}
      </Typography>

      {/* Cards — scroll-snap carousel; shows ~4 cards on desktop with the next
          one peeking at the edge instead of leaving empty space */}
      <Box
        ref={scrollRef}
        onScroll={updateScrollButtons}
        sx={{
          display: 'flex',
          gap: `${gap}px`,
          width: '100%',
          alignItems: 'flex-start',
          minHeight: isMobile ? '274px' : '334px',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {cms.testimonials.map((testimonial) => (
          <Box key={testimonial.id} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
            <TestimonialCard
              testimonial={testimonial}
              cardWidth={cardWidth}
              isMobile={isMobile}
            />
          </Box>
        ))}
      </Box>

      {/* Navigation buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: isMobile ? '10px' : '20px',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: isMobile ? '20px' : '40px',
        }}
      >
        <IconButton
          onClick={handlePrevious}
          disabled={!canGoBack}
          sx={{
            width: isMobile ? '25px' : '50px',
            height: isMobile ? '25px' : '50px',
            borderRadius: '50%',
            backgroundColor: canGoBack ? '#1D1D1B' : '#B9B9B8',
            '&:hover': { backgroundColor: canGoBack ? '#2a2a2a' : '#B9B9B8' },
            '&:disabled': { backgroundColor: '#B9B9B8' },
          }}
        >
          <Box
            component="img"
            src={Images.leftArrow}
            alt="Previous"
            sx={{
              width: isMobile ? '8px' : '15px',
              height: isMobile ? '6px' : '12px',
              filter: canGoBack ? 'brightness(0) invert(1)' : 'none',
            }}
          />
        </IconButton>

        <IconButton
          onClick={handleNext}
          disabled={!canGoNext}
          sx={{
            width: isMobile ? '25px' : '50px',
            height: isMobile ? '25px' : '50px',
            borderRadius: '50%',
            backgroundColor: canGoNext ? '#1D1D1B' : '#B9B9B8',
            '&:hover': { backgroundColor: canGoNext ? '#2a2a2a' : '#B9B9B8' },
            '&:disabled': { backgroundColor: '#B9B9B8' },
          }}
        >
          <Box
            component="img"
            src={Images.rightArrow}
            alt="Next"
            sx={{
              width: isMobile ? '8px' : '15px',
              height: isMobile ? '6px' : '12px',
              filter: canGoNext ? 'brightness(0) invert(1)' : 'none',
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TestimonialSection;
